import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ChevronRight, Image as ImageIcon, ShoppingBag, Warehouse, X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import {
  changeLocationStock, loadLocationProductStock, loadLocationStock, loadProducts, loadStockLocations,
} from './api';
import {
  Empty, Failed, Head, Loading, SearchField, WorkspaceTabs, XomicsSelect, useLeave,
} from './parts';
import { FilterMenu, isKind, kindOptions } from './ProductFilter';
import { OfficeNamed } from './StoreOffice';
import { StockChange, StockHistory, StockNow } from './StoreStock';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './productDetail.css';
import './products.css';
import './storeOffice.css';
import './stockRequests.css';

// Warehouse, Stock and Retail, Stock: what a warehouse or a retail shop holds.
// Every product of the catalogue that goes in a parcel, with how many the
// place has. A row opens the product's stock: the number (of each finish and
// size, for a product with choices), Add stock and Remove stock, and every
// change beside it, kept exactly as an office's stock is (StoreStock.js). A
// warehouse or a shop sells nothing, so nothing here has a price, and nothing
// is made here: the catalogue is where products are added.

// Only what is shipped is counted: a service or a subscription does not run out.
const keepsStock = (product) => product.shippingWeightGrams !== null;

// A warehouse or shop as the office parts name one: its name, its address.
const asNamed = (place) => ({ ...place, countryCode: place.address.countryCode });

function placeAddress(place) {
  const { line1, line2, postalCode, city, region } = place.address;
  return [line1, line2, postalCode, city, region].filter((part) => typeof part === 'string' && part.trim() !== '').join(', ');
}

// Whose stock the page is, under its title: the place by its name and where
// it is. An account that keeps every place's stock chooses the place there.
function PlaceLine({ place, places, kind, onChoose }) {
  const Icon = kind === 'warehouse' ? Warehouse : ShoppingBag;
  return (
    <div className="xo-whose">
      <Icon aria-hidden="true" />
      {places.length > 1 ? (
        <XomicsSelect className="xo-whose-choice" aria-label={tx('ui.location_stock.of')} value={String(place.id)}
          onChange={(event) => onChoose(Number(event.target.value))}>
          {places.map((entry) => <option key={entry.id} value={String(entry.id)}>{entry.name}</option>)}
        </XomicsSelect>
      ) : <strong>{place.name}</strong>}
      <address className="xo-whose-address">{placeAddress(place)}</address>
    </div>
  );
}

function StockList({ products, onOpen }) {
  if (products.length === 0) return <Empty title={tx("ui.products.nothing_matches_17e7aebf")} />;
  return (
    <div className="xo-pc-list is-place">
      <div className="xo-pc-row is-head" aria-hidden="true">
        <span>{tx('ui.store.product')}</span>
        <span>{tx("ui.products.stock_bcecf456")}</span>
        <span />
      </div>
      {products.map((product) => {
        const picture = product.mainImageThumbnailUrl ?? product.imageUrl ?? null;
        return (
          <button type="button" key={product.id} className={`xo-pc-row${product.held > 0 ? ' is-on' : ''}`} onClick={() => onOpen(product)}>
            <span className="xo-pc-name">
              <span className="xo-pc-thumb">{picture ? <img src={picture} alt="" loading="lazy" decoding="async" /> : <ImageIcon aria-hidden="true" />}</span>
              <span><strong>{product.name}</strong><small>{product.category?.name ?? ''}</small></span>
            </span>
            {product.held > 0
              ? <span className="xo-pc-number">{product.held.toLocaleString()}</span>
              : <span className="xo-pc-number is-empty">{tx('ui.products.options.out_of_stock')}</span>}
            <ChevronRight aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

// One product's stock at the place, as a sheet: the number and the history
// beside it. Each change is kept at once, in a form of its own.
function StockSheet({ product, place, kind, onClose, onChanged }) {
  const [stock, setStock] = useState(null);
  const [historyKind, setHistoryKind] = useState('');
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState(null);
  const [older, setOlder] = useState({ rows: [], hasMore: false });
  const [reading, setReading] = useState(false);
  const [change, setChange] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setReading(true);
    loadLocationProductStock(place.id, product.id, { kind: historyKind, search, sku: only })
      .then((answer) => { if (live) { setStock(answer); setOlder({ rows: [], hasMore: false }); } })
      .catch((caught) => { if (live) setError(errorMessage(caught)); })
      .finally(() => { if (live) setReading(false); });
    return () => { live = false; };
  }, [historyKind, only, place.id, product.id, search]);

  async function readOlder() {
    const last = (older.rows.length > 0 ? older.rows : stock.movements).at(-1);
    setReading(true);
    try {
      const page = await loadLocationProductStock(place.id, product.id, { kind: historyKind, search, sku: only, before: last.id });
      setOlder((had) => ({ rows: [...had.rows, ...page.movements], hasMore: page.hasMore }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setReading(false);
    }
  }

  const close = useLeave(change !== null, false, onClose);
  const picture = product.mainImageThumbnailUrl ?? product.imageUrl ?? null;

  return (<>
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <section className="xo-pm xo-store-sheet" role="dialog" aria-modal="true" aria-labelledby="xo-place-stock-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pm-head">
          <span className="xo-pm-thumb">{picture ? <img src={picture} alt="" /> : <ImageIcon aria-hidden="true" />}</span>
          <div className="xo-pm-title">
            <h2 id="xo-place-stock-title">{product.name}</h2>
            <OfficeNamed office={asNamed(place)} />
          </div>
          <div className="xo-pm-head-actions">
            <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} onClick={close}>
              <X aria-hidden="true" /></button>
          </div>
        </header>
        <div className="xo-pm-body">
          <div className={`xo-pm-panel xo-store-stock${stock?.stockPerCombination ? ' is-entries' : ''}`}>
            {stock === null ? <p className="xo-stock-none">{tx('ui.store.stock.reading')}</p> : <>
              <StockNow stock={stock} only={only} onOnly={setOnly} onChange={setChange} />
              <StockHistory stock={stock} older={older.rows} hasMore={older.rows.length > 0 ? older.hasMore : stock.hasMore}
                kind={historyKind} search={search} only={only} busy={reading}
                requestPath={kind === 'warehouse' ? '/xomics/warehouse/requests' : null}
                onKind={setHistoryKind} onSearch={setSearch} onOnly={setOnly} onMore={readOlder} />
            </>}
          </div>
        </div>
        <footer className="xo-pc-form-foot">
          {error ? <p className="xo-alert t-bad" role="alert">{error}</p> : <span />}
          <button type="button" className="xo-btn-2" onClick={close}>{tx("ui.products.close_bbfa773e")}</button>
        </footer>
      </section>
    </div>
    {change !== null && stock !== null && (
      <StockChange office={asNamed(place)} product={product} stock={stock} change={change}
        save={(body) => changeLocationStock(place.id, product.id, body)}
        onClose={() => setChange(null)}
        onDone={(after) => {
          // The answer is the whole history's first page: what narrowed it is let go.
          setHistoryKind(''); setSearch(''); setOnly(null); setOlder({ rows: [], hasMore: false });
          setStock(after); setChange(null); onChanged();
        }} />
    )}
  </>);
}

// `kind`: 'warehouse' or 'retail', the places this page is for.
export default function XomicsLocationStock({ kind }) {
  const places = useLiveResource(['locations'], useCallback(() => loadStockLocations(), []));
  const [searchParams, setSearchParams] = useSearchParams();
  const ofKind = useMemo(() => (places.data?.locations ?? []).filter((place) => place.kind === kind), [kind, places.data]);
  // The place shown: the one in the address, else the account's own, else the first.
  const wanted = Number(searchParams.get('location'));
  const place = ofKind.find((entry) => entry.id === wanted)
    ?? ofKind.find((entry) => entry.id === places.data?.ownId) ?? ofKind[0] ?? null;
  const placeId = place?.id ?? null;
  const held = useLiveResource(['products', 'locations'], useCallback(() => loadLocationStock(placeId), [placeId]), { enabled: placeId !== null });
  const catalogue = useLiveResource(['products'], useCallback(() => loadProducts(), []));
  const [filter, setFilter] = useState('all');
  const [productKind, setProductKind] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  // A product taken out of the catalogue is listed only while the place still holds some of it.
  const products = useMemo(() => {
    const byId = new Map((held.data?.products ?? []).map((row) => [row.productId, row.stock]));
    return (catalogue.data ?? []).filter(keepsStock).map((product) => ({ ...product, held: byId.get(product.id) ?? 0 }))
      .filter((product) => product.isActive || product.held > 0);
  }, [catalogue.data, held.data]);
  const inState = useCallback((product) => filter === 'all' || (filter === 'in') === (product.held > 0), [filter]);
  const ofProductKind = useMemo(() => products.filter((product) => isKind(product, productKind)), [productKind, products]);
  const inStock = useMemo(() => ofProductKind.filter((product) => product.held > 0).length, [ofProductKind]);
  const kinds = useMemo(() => kindOptions(products.filter(inState)), [inState, products]);
  // What is held first, then by name.
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return ofProductKind
      .filter((product) => needle === '' || `${product.name} ${product.category?.name ?? ''}`.toLowerCase().includes(needle))
      .filter(inState)
      .sort((left, right) => Number(right.held > 0) - Number(left.held > 0) || left.name.localeCompare(right.name));
  }, [inState, ofProductKind, query]);
  const open = products.find((product) => product.id === openId) ?? null;

  const title = tx('ui.nav.stock');
  const subtitle = kind === 'warehouse' ? tx('ui.location_stock.subtitle_warehouse') : tx('ui.location_stock.subtitle_retail');
  const head = place === null
    ? <Head title={title} subtitle={subtitle} />
    : <Head title={title} under={<PlaceLine place={place} places={ofKind} kind={kind}
      onChoose={(id) => setSearchParams({ location: String(id) }, { replace: true })} />} />;

  if (places.error) return <Failed message={places.error} onRetry={places.refresh} />;
  if (places.data === null) return <Loading label={tx("ui.products.reading_the_catalogue_2a05727e")} />;
  if (place === null) {
    return <div className="xo-products-page xo-pc-page">{head}
      <Empty title={kind === 'warehouse' ? tx('ui.location_stock.no_warehouse') : tx('ui.location_stock.no_shop')} /></div>;
  }
  if (held.error) return <Failed message={held.error} onRetry={held.refresh} />;
  if (catalogue.error) return <Failed message={catalogue.error} onRetry={catalogue.refresh} />;
  if (held.data === null || catalogue.data === null) return <Loading label={tx("ui.products.reading_the_catalogue_2a05727e")} />;

  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      <div className="xo-product-navigation">
        <div className="xo-product-toolbar">
          <SearchField className="xo-product-search" value={query} onChange={setQuery} placeholder={tx("ui.products.search_products_4412bfe5")} />
          <div className="xo-product-toolbar-actions">
            <FilterMenu value={productKind} onChange={setProductKind} options={kinds} label={tx('ui.store.filter')} />
          </div>
        </div>
        <div className="xo-product-section-row">
          <WorkspaceTabs className="xo-product-sections" value={filter} onChange={setFilter} label={tx("ui.products.stock_bcecf456")}
            tabs={[
              { value: 'all', label: tx('ui.store.all'), count: ofProductKind.length },
              { value: 'in', label: tx('ui.location_stock.in_stock'), count: inStock },
              { value: 'out', label: tx('ui.products.options.out_of_stock'), count: ofProductKind.length - inStock },
            ]} />
        </div>
      </div>
      <div className={held.loading ? 'xo-store-turning' : undefined}>
        <StockList products={shown} onOpen={(product) => setOpenId(product.id)} />
      </div>
      {open !== null && <StockSheet key={`${placeId}-${open.id}`} product={open} place={place} kind={kind}
        onClose={() => setOpenId(null)} onChanged={() => held.refresh()} />}
    </div>
  );
}
